import { css, CSSResult, CSSResultGroup, unsafeCSS } from "lit";

import { ButtonSize } from "../ButtonSize";
import { ButtonSizeToken } from "./ButtonSizeToken";

/** @private */
function buttonStyle(size: ButtonSize): CSSResult {
  const selector = unsafeCSS(`:is(:state(--${size}), :--${size})`);
  return css`
    :host(${selector}) .base {
      height: ${ButtonSizeToken[size].containerHeight};
    }
    :host(${selector}) .wrapper {
      padding-inline-start: calc(${ButtonSizeToken[size].leadingSpace} - calc(var(--_adjacent-shrink, 0px) / 2));
      padding-inline-end: calc(${ButtonSizeToken[size].trailingSpace} - calc(var(--_adjacent-shrink, 0px) / 2));
      column-gap: ${ButtonSizeToken[size].iconLabelSpace};
    }
    :host(${selector}) .label {
      font-size: ${ButtonSizeToken[size].labelTextFontSize};
      font-weight: ${ButtonSizeToken[size].labelTextFontWeight};
      line-height: ${ButtonSizeToken[size].labelTextLineHeight};
      letter-spacing: ${ButtonSizeToken[size].labelTextTracking};
    }
    :host(${selector}) .icon {
      font-size: ${ButtonSizeToken[size].iconSize};
      --m3e-icon-size: ${ButtonSizeToken[size].iconSize};
    }
    :host(${selector}) .base {
      outline-offset: calc(0px - ${ButtonSizeToken[size].outlineThickness});
      outline-width: ${ButtonSizeToken[size].outlineThickness};
    }
    :host(:not(:is(:state(--connected), :--connected))${selector}:is(:state(--rounded), :--rounded)) .base {
      border-start-start-radius: var(
        --m3e-button-start-start-shape,
        var(--_button-shape, ${ButtonSizeToken[size].shapeRound})
      );
      border-end-start-radius: var(
        --m3e-button-end-start-shape,
        var(--_button-shape, ${ButtonSizeToken[size].shapeRound})
      );
      border-start-end-radius: var(
        --m3e-button-start-end-shape,
        var(--_button-shape, ${ButtonSizeToken[size].shapeRound})
      );
      border-end-end-radius: var(--m3e-button-end-end-shape, var(--_button-shape, ${ButtonSizeToken[size].shapeRound}));
    }
    :host(
        :is(:state(--connected), :--connected)${selector}:is(:state(--rounded), :--rounded)[toggle][selected]:not(
            :is(:state(--pressed), :--pressed)
          )
      )
      .base {
      border-start-start-radius: var(
        --m3e-button-start-start-shape,
        var(--_button-shape, ${ButtonSizeToken[size].shapeRound})
      );
      border-end-start-radius: var(
        --m3e-button-end-start-shape,
        var(--_button-shape, ${ButtonSizeToken[size].shapeRound})
      );
      border-start-end-radius: var(
        --m3e-button-start-end-shape,
        var(--_button-shape, ${ButtonSizeToken[size].shapeRound})
      );
      border-end-end-radius: var(--m3e-button-end-end-shape, var(--_button-shape, ${ButtonSizeToken[size].shapeRound}));
    }
    :host(
        :not(:is(:state(--connected), :--connected))${selector}:is(:state(--rounded), :--rounded)[toggle][selected]:not(
            :is(:state(--pressed), :--pressed)
          )
      )
      .base {
      border-start-start-radius: var(--m3e-button-start-start-shape, ${ButtonSizeToken[size].selectedShapeRound});
      border-end-start-radius: var(--m3e-button-end-start-shape, ${ButtonSizeToken[size].selectedShapeRound});
      border-start-end-radius: var(--m3e-button-start-end-shape, ${ButtonSizeToken[size].selectedShapeRound});
      border-end-end-radius: var(--m3e-button-end-end-shape, ${ButtonSizeToken[size].selectedShapeRound});
    }
    :host(:not(:is(:state(--connected), :--connected))${selector}:is(:state(--square), :--square)) .base {
      border-start-start-radius: var(--m3e-button-start-start-shape, ${ButtonSizeToken[size].shapeSquare});
      border-end-start-radius: var(--m3e-button-end-start-shape, ${ButtonSizeToken[size].shapeSquare});
      border-start-end-radius: var(--m3e-button-start-end-shape, ${ButtonSizeToken[size].shapeSquare});
      border-end-end-radius: var(--m3e-button-end-end-shape, ${ButtonSizeToken[size].shapeSquare});
    }
    :host(
        :not(:is(:state(--connected), :--connected))${selector}:is(:state(--square), :--square)[toggle][selected]:not(
            :is(:state(--pressed), :--pressed)
          )
      )
      .base {
      border-start-start-radius: var(
        --m3e-button-start-start-shape,
        var(--_button-shape, ${ButtonSizeToken[size].selectedShapeSquare})
      );
      border-end-start-radius: var(
        --m3e-button-end-start-shape,
        var(--_button-shape, ${ButtonSizeToken[size].selectedShapeSquare})
      );
      border-start-end-radius: var(
        --m3e-button-start-end-shape,
        var(--_button-shape, ${ButtonSizeToken[size].selectedShapeSquare})
      );
      border-end-end-radius: var(
        --m3e-button-end-end-shape,
        var(--_button-shape, ${ButtonSizeToken[size].selectedShapeSquare})
      );
    }
    :host(:not(:is(:state(--connected), :--connected))${selector}:is(:state(--pressed), :--pressed)) .base {
      border-start-start-radius: var(--m3e-button-start-start-shape, ${ButtonSizeToken[size].shapePressedMorph});
      border-end-start-radius: var(--m3e-button-end-start-shape, ${ButtonSizeToken[size].shapePressedMorph});
      border-start-end-radius: var(--m3e-button-start-end-shape, ${ButtonSizeToken[size].shapePressedMorph});
      border-end-end-radius: var(--m3e-button-end-end-shape, ${ButtonSizeToken[size].shapePressedMorph});
    }
    :host(:is(:state(--connected), :--connected)${selector}:is(:state(--rounded), :--rounded):not([toggle][selected]))
      .base {
      border-start-start-radius: var(
        --m3e-button-start-start-shape,
        var(--_button-rounded-start-shape, var(--_button-shape, ${ButtonSizeToken[size].shapeRound}))
      );
      border-end-start-radius: var(
        --m3e-button-end-start-shape,
        var(--_button-rounded-start-shape, var(--_button-shape, ${ButtonSizeToken[size].shapeRound}))
      );
      border-start-end-radius: var(
        --m3e-button-start-end-shape,
        var(--_button-rounded-end-shape, var(--_button-shape, ${ButtonSizeToken[size].shapeRound}))
      );
      border-end-end-radius: var(
        --m3e-button-end-end-shape,
        var(--_button-rounded-end-shape, var(--_button-shape, ${ButtonSizeToken[size].shapeRound}))
      );
    }
    :host(:is(:state(--connected), :--connected)${selector}:is(:state(--square), :--square)) .base {
      border-start-start-radius: var(
        --m3e-button-start-start-shape,
        var(--_button-square-start-shape, ${ButtonSizeToken[size].shapeSquare})
      );
      border-end-start-radius: var(
        --m3e-button-end-start-shape,
        var(--_button-square-start-shape, ${ButtonSizeToken[size].shapeSquare})
      );
      border-start-end-radius: var(
        --m3e-button-start-end-shape,
        var(--_button-square-end-shape, ${ButtonSizeToken[size].shapeSquare})
      );
      border-end-end-radius: var(
        --m3e-button-end-end-shape,
        var(--_button-square-end-shape, ${ButtonSizeToken[size].shapeSquare})
      );
    }
    :host(
        :is(:state(--connected), :--connected)${selector}:is(:state(--square), :--square)[toggle][selected]:not(
            :is(:state(--pressed), :--pressed)
          )
      )
      .base {
      border-start-start-radius: var(
        --m3e-button-start-start-shape,
        var(--_button-shape, ${ButtonSizeToken[size].selectedShapeSquare})
      );
      border-end-start-radius: var(
        --m3e-button-end-start-shape,
        var(--_button-shape, ${ButtonSizeToken[size].selectedShapeSquare})
      );
      border-start-end-radius: var(
        --m3e-button-start-end-shape,
        var(--_button-shape, ${ButtonSizeToken[size].selectedShapeSquare})
      );
      border-end-end-radius: var(
        --m3e-button-end-end-shape,
        var(--_button-shape, ${ButtonSizeToken[size].selectedShapeSquare})
      );
    }
    :host(:is(:state(--connected), :--connected)${selector}:is(:state(--pressed), :--pressed)) .base {
      border-start-start-radius: var(
        --m3e-button-start-start-shape,
        var(--_button-start-shape-pressed-morph, ${ButtonSizeToken[size].shapePressedMorph})
      );
      border-end-start-radius: var(
        --m3e-button-end-start-shape,
        var(--_button-start-shape-pressed-morph, ${ButtonSizeToken[size].shapePressedMorph})
      );
      border-start-end-radius: var(
        --m3e-button-start-end-shape,
        var(--_button-end-shape-pressed-morph, ${ButtonSizeToken[size].shapePressedMorph})
      );
      border-end-end-radius: var(
        --m3e-button-end-end-shape,
        var(--_button-end-shape-pressed-morph, ${ButtonSizeToken[size].shapePressedMorph})
      );
    }
  `;
}

/**
 * Size variant styles for `M3eButtonElement`.
 * @internal
 */
export const ButtonSizeStyle: CSSResultGroup = [
  buttonStyle("extra-small"),
  buttonStyle("small"),
  buttonStyle("medium"),
  buttonStyle("large"),
  buttonStyle("extra-large"),
];
